import * as m from '@staiter/i18n';
import { NotificationTrigger } from '@staiter/shared/task-module';
import { useInfiniteQuery, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import {
  Archive,
  ArrowUpRight,
  Check,
  CheckCheck,
  CircleSlash,
  Columns3,
  Eye,
  EyeOff,
  Mail,
  MailOpen,
  Undo2,
  User,
  X,
} from 'lucide-react';
import type React from 'react';
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { PageHeader } from '~/components/data';
import { Table, type TableColumn } from '~/components/data/Table';
import { emitToast } from '~/components/feedback/toast-bus';
import { Button } from '~/components/primitives';
import { Checkbox } from '~/components/primitives/Checkbox';
import { TaskPopover } from '~/components/tasks/inline-editors/TaskPopover';
import { NotifColumnFilter } from '~/components/tasks/notifications/NotifColumnFilter';
import { approvalIdOf } from '~/components/tasks/notifications/NotificationCard';
import { NotificationSettingsPanel } from '~/components/tasks/notifications/NotificationSettingsPanel';
import {
  accessRequestModule,
  approvalSubjectParts,
  approvalSubjectText,
  episodeDueChip,
  episodeEventCount,
  formatIsoDate,
  isApprovalSubject,
  isDigest,
  isProjectSubject,
  notifLabel,
  triggerIcon,
  triggerLabel,
} from '~/components/tasks/notifications/notificationMeta';
import { useLocale } from '~/lib/hooks/useLocale';
import { moduleLabel } from '~/lib/rbac/module-label';
import { notifTime, notifTimeTitle } from '~/lib/tasks/format/relativeTime';
import { isTypingTarget } from '~/lib/tasks/notifications/hotkeyGuard';
import {
  NOTIFICATIONS_BELL_KEY,
  NOTIFICATIONS_KEY,
  type NotificationFilters,
  type NotificationItem,
  type NotificationListData,
  dismissNotifications,
  dismissReadNotifications,
  fetchNotifications,
  markAllNotificationsRead,
  markNotificationsRead,
  markNotificationsUnread,
  notificationsListKey,
  undismissNotifications,
} from '~/lib/tasks/queries/useNotifications';
import { useTimerFollowUpStore } from '~/lib/tasks/stores/timerFollowUp';
import { useNotificationCardStore } from '~/store/notificationCard';
import { type NotifTableSort, useNotificationsTableStore } from '~/store/notificationsTable';
import { useUiStore } from '~/store/ui';
import shared from './_auth._shared.module.css';
import styles from './_auth.notifications.module.css';

/**
 * F1 + F11 (sla-lifecycle §4.5, §9.2) — search-параметры центра.
 *
 * Схема — КОНСТРУКТОР, а не whitelist: она собирает объект заново и теряет всё,
 * что здесь не перечислено `[S-C4-3]`. Поэтому каждый новый параметр обязан быть
 * добавлен явно — иначе редирект/deep-link приземлится на голом списке.
 *
 *   actionable — историческое «?actionable=overdue» (клик агрегата);
 *   project    — префильтр по проекту (клик дайджеста, deep-link);
 *   triggers   — csv валидных NotificationTrigger;
 *   resolved   — показывать «неактуальные» (F6);
 *   tab        — вкладка «Настройки» (F11).
 */
type NotifSearch = {
  actionable?: 'overdue';
  project?: string;
  /** csv — URL остаётся коротким и совпадает с href тоста дайджеста (§3.3). */
  triggers?: string;
  /** Принимаем `1`/`true` из внешних ссылок, наружу пишем boolean (TanStack
   *  иначе экранирует строку в `%221%22` — уродливый и хрупкий URL). */
  resolved?: boolean;
  /**
   * F17 (§3.5) [UF-4] — сегмент «Скрытые». Явно, как и всё остальное: схема
   * собирает объект заново, и незаписанный здесь параметр не переживёт ни
   * редиректа, ни расшаренной ссылки `[S-C4-3]`.
   */
  dismissed?: boolean;
  tab?: 'settings';
};

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const SearchSchema = (s: Record<string, unknown>): NotifSearch => {
  const out: NotifSearch = {};
  if (s.actionable === 'overdue') out.actionable = 'overdue';
  if (typeof s.project === 'string' && UUID_RE.test(s.project)) out.project = s.project;
  if (typeof s.triggers === 'string') {
    const valid = parseTriggers(s.triggers);
    if (valid.length > 0) out.triggers = valid.join(',');
  }
  // Внешние ссылки пишут `?resolved=1`, наш navigate — `?resolved=true`; парсер
  // TanStack отдаёт их как число / boolean / строку — принимаем все три формы.
  if (s.resolved === 1 || s.resolved === '1' || s.resolved === true || s.resolved === 'true') {
    out.resolved = true;
  }
  if (s.dismissed === 1 || s.dismissed === '1' || s.dismissed === true || s.dismissed === 'true') {
    out.dismissed = true;
  }
  if (s.tab === 'settings') out.tab = 'settings';
  return out;
};

/** csv → только валидные `NotificationTrigger` (мусор из URL молча отбрасываем). */
function parseTriggers(csv: string): string[] {
  return csv
    .split(',')
    .map((t) => t.trim())
    .filter((t) => (NotificationTrigger.options as readonly string[]).includes(t));
}

export const Route = createFileRoute('/_auth/notifications')({
  staticData: { minMainWidth: 1068 },
  validateSearch: SearchSchema,
  component: NotificationsCenter,
});

type FilterMode = 'all' | 'actionable' | 'info';
type StatusFilter = 'unread' | 'read' | null;

/** Колонки таблицы (id совпадает с B11 sort-ключами для сортируемых). */
type ColId = 'trigger' | 'task' | 'project' | 'who' | 'count' | 'created_at' | 'status' | 'dismiss';
const SORTABLE: ReadonlySet<ColId> = new Set(['trigger', 'project', 'count', 'created_at']);

/** id триггеров для фильтра «Тип». */
const TRIGGER_OPTIONS = NotificationTrigger.options.map((t: string) => ({
  value: t,
  label: triggerLabel(t),
}));

function NotificationsCenter(): JSX.Element {
  const search = Route.useSearch();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const locale = useLocale();
  const openCard = useNotificationCardStore((s) => s.openCard);
  const requestTimeSection = useTimerFollowUpStore((s) => s.requestTimeSection);

  const widths = useNotificationsTableStore((s) => s.widths);
  const hidden = useNotificationsTableStore((s) => s.hidden);
  const sort = useNotificationsTableStore((s) => s.sort);
  const setWidth = useNotificationsTableStore((s) => s.setWidth);
  const toggleHidden = useNotificationsTableStore((s) => s.toggleHidden);
  const setSort = useNotificationsTableStore((s) => s.setSort);

  const [mode, setMode] = useState<FilterMode>(
    search.actionable === 'overdue' ? 'actionable' : 'all',
  );
  const [statusFilter, setStatusFilter] = useState<StatusFilter>(null);
  // F1 — фильтры инициализируются ИЗ URL: без этого клик дайджеста и любая
  // расшаренная ссылка приземлялись на неотфильтрованном списке.
  const [triggerFilter, setTriggerFilter] = useState<string[]>(
    search.triggers ? parseTriggers(search.triggers) : [],
  );
  const [projectFilter, setProjectFilter] = useState<string | null>(search.project ?? null);
  const [showResolved, setShowResolved] = useState<boolean>(search.resolved === true);
  /**
   * F17 — сегмент «Скрытые». Отдельная ось, а не ещё одно значение `mode`:
   * скрытые не пересекаются ни с «Требует действия», ни с «Для сведения» — это
   * не срез содержимого, а другой ящик.
   */
  const [showDismissed, setShowDismissed] = useState<boolean>(search.dismissed === true);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const tab = search.tab === 'settings' ? 'settings' : 'list';
  const actionablePreset = search.actionable;

  // F1 — и синхронизируются ОБРАТНО в URL (шэрабельность состояния).
  // biome-ignore lint/correctness/useExhaustiveDependencies: navigate стабилен
  useEffect(() => {
    if (tab === 'settings') return; // вкладка настроек фильтров не имеет
    const next: NotifSearch = {
      ...(actionablePreset ? { actionable: actionablePreset } : {}),
      ...(projectFilter ? { project: projectFilter } : {}),
      ...(triggerFilter.length ? { triggers: triggerFilter.join(',') } : {}),
      ...(showResolved ? { resolved: true } : {}),
      ...(showDismissed ? { dismissed: true } : {}),
    };
    void navigate({ to: '/notifications', search: next, replace: true });
  }, [projectFilter, triggerFilter, showResolved, showDismissed, tab, actionablePreset]);

  useEffect(() => {
    document.title = m.tm_notif_center_title();
  }, []);

  // Дефолтная сортировка (sort===null) → cursor-режим, байт-в-байт совместимый с
  // текущим FE (B11); любая иная → offset-режим сервера.
  const backendFilters: NotificationFilters = {
    ...(mode === 'actionable' ? { actionable_only: true } : {}),
    // D5.3 (Y-4) — «Для сведения» ушло на сервер. Клиентский фильтр по уже
    // загруженной странице показывал не всё для сведения, а лишь то из первых
    // тридцати, что не actionable, — и расходился с собственным счётчиком.
    ...(mode === 'info' ? { info_only: true } : {}),
    ...(statusFilter === 'unread' ? { unread_only: true } : {}),
    ...(statusFilter === 'read' ? { read_only: true } : {}),
    ...(triggerFilter.length ? { trigger: triggerFilter } : {}),
    ...(projectFilter ? { project_id: projectFilter } : {}),
    // F6 (§4.4 `[S-FE-9]`) — фильтр resolved ОРТОГОНАЛЕН вкладкам: тумблер работает
    // и во «Всех», и в «Требует действия», и в «Для сведения». Формула вкладки —
    // actionable AND (NOT resolved OR тумблер включён); первая часть — на сервере
    // (`actionable_only`), вторая — здесь.
    ...(showResolved ? { include_resolved: true } : {}),
    ...(showDismissed ? { dismissed: true } : {}),
    ...(sort ? { sort: sort.id, order: sort.dir } : {}),
  };

  const isDefaultSort = sort === null;

  const query = useInfiniteQuery({
    queryKey: notificationsListKey(backendFilters),
    queryFn: ({ pageParam }) => fetchNotifications(pageParam, backendFilters),
    initialPageParam: {} as { cursor?: string | null; offset?: number },
    getNextPageParam: (last) =>
      isDefaultSort
        ? last.next_cursor
          ? { cursor: last.next_cursor }
          : undefined
        : last.next_offset != null
          ? { offset: last.next_offset }
          : undefined,
  });

  const rows = useMemo(() => query.data?.pages.flatMap((p) => p.notifications) ?? [], [query.data]);

  /**
   * D5.3 (Y-4) — размеры сегментов приходят с сервера и посчитаны по ТЕМ ЖЕ
   * фильтрам (тип, проект, прочтение, неактуальные), минус сама сегментная ось.
   * Поэтому «Все» = «Требует действия» + «Для сведения» в любом состоянии
   * фильтров, и число не растёт от нажатий «Показать ещё».
   */
  const segmentCounts = query.data?.pages[0]?.segment_counts ?? null;

  /**
   * F17, раунд 1 гейта (обе половины, независимо): чип «Скрытые» без числа —
   * единственный выход к скрытому, который не сообщает, есть ли там что-нибудь.
   * Пустой ящик и ящик на сорок записей выглядели одинаково, и «скрытое пропало
   * бесследно» оставалось честным описанием.
   *
   * Считается отдельным лёгким запросом (одна строка выдачи, число берётся из
   * `segment_counts` под тем же фильтром) — общий ответ списка его не несёт: там
   * счётчики считаются ПО ТЕКУЩЕЙ выборке, а скрытые в ней и не должны быть.
   */
  const dismissedCountQuery = useQuery({
    queryKey: [...NOTIFICATIONS_KEY, 'dismissed-count'],
    queryFn: () => fetchNotifications({}, { dismissed: true }, 1),
    staleTime: 30_000,
  });
  const dismissedCount = dismissedCountQuery.data?.segment_counts?.all ?? null;

  /**
   * Знаменатель выдачи (offset-режим сервера). В cursor-режиме сервер `total`
   * не считает — тогда строку «показано N из M» не показываем вовсе, а не
   * подставляем длину загруженного вместо знаменателя.
   */
  const loadedTotal = query.data?.pages[0]?.total ?? null;

  // Опции фильтра «Проект» — из загруженных строк (реальный task.project_id).
  const projectOptions = useMemo(() => {
    const map = new Map<string, string>();
    for (const n of rows) {
      if (n.task_project_id && n.project_name) map.set(n.task_project_id, n.project_name);
    }
    return [...map.entries()].map(([value, label]) => ({ value, label }));
  }, [rows]);

  function invalidate(): void {
    void qc.invalidateQueries({ queryKey: NOTIFICATIONS_KEY });
    void qc.invalidateQueries({ queryKey: NOTIFICATIONS_BELL_KEY });
  }

  function goToTask(n: NotificationItem): void {
    if (!n.task_id) return;
    // Е-А5б — «таймер не остановлен» ведёт не «на задачу вообще», а туда, где
    // этот таймер останавливают.
    if (n.trigger === 'timer_reminder') requestTimeSection(n.task_id);
    void navigate({ to: '/work-tasks/task/$taskId', params: { taskId: n.task_id } });
  }

  /** F8 — клик по дайджесту разворачивает свод в его составляющие (§3.3). */
  function goToProjectNotifications(projectId: string): void {
    setProjectFilter(projectId);
    setTriggerFilter(['overdue', 'due_soon']);
    setMode('all');
  }

  async function toggleUnread(n: NotificationItem): Promise<void> {
    if (n.read_at === null) await markNotificationsRead([n.id]);
    else await markNotificationsUnread([n.id]);
    invalidate();
  }

  async function bulkRead(): Promise<void> {
    if (selected.size === 0) return;
    await markNotificationsRead([...selected]);
    setSelected(new Set());
    invalidate();
  }

  async function bulkUnread(): Promise<void> {
    if (selected.size === 0) return;
    await markNotificationsUnread([...selected]);
    setSelected(new Set());
    invalidate();
  }

  /**
   * F17 (§3.5) [UF-4] — скрытие/возврат.
   *
   * Строки убираются ОПТИМИСТИЧНО из уже загруженных страниц: скрытие — жест
   * уборки, и ждать круга «инвалидация → запрос → перерисовка» ради него значит
   * оставить человека смотреть на то, что он только что убрал. Ответ сервера
   * приходит числом реально изменённых строк — оно и идёт в тост, а не длина
   * запроса: строка, скрытая в другой вкладке, счётчик не увеличивает.
   */
  function removeRowsFromCache(ids: ReadonlySet<string>): void {
    qc.setQueryData(
      notificationsListKey(backendFilters),
      (prev: { pages: NotificationListData[]; pageParams: unknown[] } | undefined) =>
        prev === undefined
          ? prev
          : {
              ...prev,
              pages: prev.pages.map((page) => ({
                ...page,
                notifications: page.notifications.filter((n) => !ids.has(n.id)),
              })),
            },
    );
  }

  async function dismiss(ids: string[], title?: string): Promise<void> {
    if (ids.length === 0) return;
    const set = new Set(ids);
    removeRowsFromCache(set);
    setSelected((prev) => new Set([...prev].filter((id) => !set.has(id))));
    try {
      const updated = await dismissNotifications(ids);
      /**
       * Раунд 1 гейта (UX-блокер): скрытие непрочитанного проходило молча, и
       * цена ошибки выглядела невозвратной — осторожный человек просто не стал
       * бы скрывать. Возврат стоит В ТОМ ЖЕ тосте: путь «найти сегмент → найти
       * строку → вернуть» откатом не является.
       */
      emitToast({
        variant: 'success',
        /**
         * Раунд 4 (UX): «Скрыто 1 уведомление» не называло предмет, и отмена была
         * слепой. При одной строке в тосте стоит её заголовок; при пачке — счёт,
         * потому что перечислять десять заголовков в тосте некуда.
         */
        title:
          updated === 1 && title
            ? m.tm_notif_toast_dismissed_named({ title })
            : m.tm_notif_toast_dismissed({ count: updated }),
        action: { label: m.tm_notif_undismiss(), onClick: () => void undismiss(ids) },
      });
    } finally {
      // Инвалидация в любом исходе: при ошибке она возвращает строки на место,
      // при успехе — обновляет счётчики колокольчика и сегментов.
      invalidate();
    }
  }

  async function undismiss(ids: string[]): Promise<void> {
    if (ids.length === 0) return;
    const set = new Set(ids);
    removeRowsFromCache(set);
    setSelected((prev) => new Set([...prev].filter((id) => !set.has(id))));
    try {
      const updated = await undismissNotifications(ids);
      emitToast({ variant: 'success', title: m.tm_notif_toast_undismissed({ count: updated }) });
    } finally {
      invalidate();
    }
  }

  async function dismissRead(): Promise<void> {
    const { updated, ids } = await dismissReadNotifications();
    // Ноль — не ошибка, а сообщение: прочитанных не было. Молчание здесь читалось
    // бы как «кнопка не сработала».
    emitToast(
      updated === 0
        ? { variant: 'info', title: m.tm_notif_toast_nothing_dismissed() }
        : {
            variant: 'success',
            title: m.tm_notif_toast_dismissed({ count: updated }),
            /**
             * Раунд 2 (UX): возврат касается ИМЕННО этой партии — ручка отдаёт
             * id тех строк, которые скрыла она сама. «Вернуть всё скрытое»
             * вернуло бы и то, что человек убирал раньше и осознанно.
             */
            ...(ids.length > 0
              ? { action: { label: m.tm_notif_undismiss(), onClick: () => void undismiss(ids) } }
              : {}),
          },
    );
    setSelected(new Set());
    invalidate();
  }

  async function bulkDismiss(): Promise<void> {
    await (showDismissed ? undismiss([...selected]) : dismiss([...selected]));
  }

  /**
   * D5.3 (Y-4) — клавиатура строки инбокса: `x` выделить/снять, `e` —
   * прочитано/непрочитано. Раскладко-независимо (`e.code`, как Ctrl+Z в FE-4):
   * на русской раскладке те же клавиши дают «ч» и «у».
   */
  function onRowKey(n: NotificationItem, e: React.KeyboardEvent<HTMLElement>): void {
    if (e.ctrlKey || e.metaKey || e.altKey) return;
    if (e.code === 'KeyX') {
      e.preventDefault();
      setSelected((prev) => {
        const next = new Set(prev);
        if (next.has(n.id)) next.delete(n.id);
        else next.add(n.id);
        return next;
      });
      return;
    }
    if (e.code === 'KeyE') {
      e.preventDefault();
      void toggleUnread(n);
      return;
    }
    /**
     * F17 [П21] — `h` скрывает строку (в сегменте «Скрытые» — возвращает).
     *
     * Страж по цели события обязателен: без него буква «h», набранная в поле
     * фильтра или в инлайн-редакторе, убирала бы строку под курсором. Проверяем
     * САМУ цель, а не активный элемент: обработчик висит на строке, и цель — то,
     * во что человек печатает.
     */
    if (e.code === 'KeyH') {
      const target = e.target;
      if (isTypingTarget(target)) return;
      e.preventDefault();
      void (showDismissed ? undismiss([n.id]) : dismiss([n.id], notifLabel(n)));
    }
  }

  /**
   * Раунд 4 гейта — горизонтальные границы дока панели держатся в переменных.
   *
   * Панель обязана быть `fixed` (иначе она уезжает за нижнюю кромку: `main`
   * объявлен прокручиваемым, но на обычных маршрутах не прокручивается, и
   * `sticky` внутри него не срабатывает вовсе), но `fixed` считает координаты
   * от ОКНА, а таблица центрируется в контентной области. Отсюда синхронизация:
   * левый край и ширина берутся у самого контейнера страницы, поэтому центр
   * панели совпадает с центром таблицы при любой ширине рельса.
   */
  const pageRef = useRef<HTMLDivElement>(null);
  /**
   * F17, раунд 4 гейта: тост и панель массовых действий — два независимых `fixed`
   * слоя с одинаковым низом, и тост лёг поверх панели, срезав третье действие
   * ровно в те секунды, пока живо окно отмены. Поверхность объявляет высоту
   * своего дока, а тост встаёт НАД ним (см. `Toast.module.css`).
   */
  const bulkBarRef = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const root = document.documentElement;
    if (selected.size === 0) {
      root.style.removeProperty('--toast-stack-offset');
      return;
    }
    const h = bulkBarRef.current?.getBoundingClientRect().height ?? 0;
    /**
     * Высота панели + её собственный нижний отступ (`.bulkDock { bottom: --space-6 }`
     * = 32px, замерено на стенде: низ дока 1080 при высоте окна 1112): иначе тост
     * сядет на кромку панели. Число названо здесь явно, потому что читается оно из
     * ДРУГОГО модуля — молчаливая связь между двумя файлами разъехалась бы при
     * первой же правке отступа дока.
     */
    const DOCK_BOTTOM_PX = 32;
    root.style.setProperty('--toast-stack-offset', `${Math.round(h) + DOCK_BOTTOM_PX}px`);
    return () => {
      root.style.removeProperty('--toast-stack-offset');
    };
  }, [selected.size]);
  // Разворачивание рельса СДВИГАЕТ контейнер, не меняя его размер (он упёрт в
  // потолок 1440px), поэтому наблюдатель размера этого не видит — замер давал
  // устойчивое расхождение 92px после каждого переключения. Подписка на само
  // состояние рельса — единственный источник, который сообщает о сдвиге.
  const sidebarCollapsed = useUiStore((s) => s.sidebarCollapsed);
  useLayoutEffect(() => {
    // Значение не участвует в расчёте — оно ТРИГГЕР пересчёта геометрии: сдвиг
    // контейнера при смене рельса не виден ни наблюдателю размера, ни рендеру
    // самого маршрута. Читаем явно, чтобы зависимость не выглядела лишней.
    void sidebarCollapsed;
    const el = pageRef.current;
    if (!el) return;
    const sync = (): void => {
      const r = el.getBoundingClientRect();
      el.style.setProperty('--bulk-dock-left', `${Math.round(r.left)}px`);
      el.style.setProperty('--bulk-dock-width', `${Math.round(r.width)}px`);
    };
    sync();
    /*
     * `sidebarCollapsed` меняется ЕЩЁ ДО анимации рельса, поэтому одного
     * пересчёта мало. Ни `transitionend`, ни наблюдатель размера тут не
     * помогают — оба проверены замером и оба давали устойчивое расхождение
     * 92px: событие прилетает от вложенных свойств раньше, чем устоится
     * геометрия контейнера, а сдвиг без изменения размера наблюдатель не видит
     * по определению. Поэтому короткий догоняющий цикл по кадрам: он покрывает
     * анимацию любой длительности и сам себя останавливает.
     */
    let raf = 0;
    // 1200мс — с запасом: замер показал, что контейнер устаивается на ~800-й
    // миллисекунде после переключения рельса.
    const until = performance.now() + 1200;
    const chase = (): void => {
      sync();
      if (performance.now() < until) raf = requestAnimationFrame(chase);
    };
    raf = requestAnimationFrame(chase);
    const ro = new ResizeObserver(sync);
    ro.observe(el);
    if (el.parentElement) ro.observe(el.parentElement);
    window.addEventListener('resize', sync);
    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
      window.removeEventListener('resize', sync);
    };
  }, [sidebarCollapsed]);

  // D5.3 — Esc выходит из режима выделения. Подсказка о нём стоит в самой панели:
  // клавиша, о которой не написано, для пользователя не существует.
  //
  // NC-4 (свод tails-sweep-2, заход 5) — `Ctrl/Cmd+A` выбирает ВСЁ,
  // что сейчас в выдаче. §11.3 STAITER_UX требует «выбрать все N в фильтре», а на
  // экране был только крестик на строке: массовое действие над сотней уведомлений
  // собиралось сотней кликов. Выделяем именно загруженные строки (`rows`), а не
  // «все на сервере»: обещать выбор того, чего пользователь не видел, — это тот же
  // класс, что чип, считающий не по показанному (NC-16).
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape' && selected.size > 0) {
        setSelected(new Set());
        return;
      }
      if ((e.ctrlKey || e.metaKey) && (e.key === 'a' || e.key === 'A' || e.code === 'KeyA')) {
        // В поле ввода `Ctrl+A` принадлежит тексту, а не списку.
        const el = document.activeElement;
        const tag = el?.tagName;
        if (
          tag === 'INPUT' ||
          tag === 'TEXTAREA' ||
          (el as HTMLElement | null)?.isContentEditable
        ) {
          return;
        }
        if (rows.length === 0) return;
        e.preventDefault();
        setSelected((prev) =>
          prev.size === rows.length ? new Set() : new Set(rows.map((r) => r.id)),
        );
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [selected.size, rows]);

  async function readAll(): Promise<void> {
    await markAllNotificationsRead();
    setSelected(new Set());
    invalidate();
  }

  // ── Колонки ────────────────────────────────────────────────────────
  const allColumns: TableColumn<NotificationItem>[] = [
    {
      id: 'trigger',
      // Раунд 3 гейта — порядок назван словом. Каретка на колонке «Тип» обещает
      // алфавит, а строки идут рангом критичности; оба ревьюера прочли это как
      // обещание, которого сортировка не выполняет. Подпись появляется только
      // когда сортировка активна — иначе она врала бы о текущем порядке.
      header: (
        <span title={m.tm_notif_sort_criticality_hint()}>
          {m.tm_notif_col_type()}
          {/* Разделитель обязателен: без него «Тип по критичности» слипается в
              одно имя колонки, и подпись порядка читается как название поля. */}
          {sort?.id === 'trigger' && (
            <span className={styles.sortNote}> · {m.tm_notif_sort_criticality()}</span>
          )}
        </span>
      ),
      sortable: true,
      // D5.1 — ширина под самую длинную подпись обеих локалей («Комментарий в
      // отслеживаемой задаче» / «Просрочено · повторно»). Раньше колонка получала
      // лишнее пропорциональным дележом остатка; после появления поглотителя
      // остатка ей не достаётся, и объявленное число стало реальным.
      // 336 — замер: «Комментарий в отслеживаемой задаче» требует 319px содержимого.
      // С-G Р.6, круг 2: 264 и перенос подписи (`.typeCell`) — на 1280 предмету строки не
      // хватало места, а длинная подпись встречается в одном типе из пятнадцати.
      width: 264,
      accessor: (n) => (
        // Ступени эпизода различаются НЕ только словом: просрочка — отклонение,
        // и по §1.3 отклонение носит семантический цвет. Иконки Clock и
        // CircleSlash при 15px почти неразличимы, монохромная лестница читалась
        // только чтением каждой строки (UI-ревю С2).
        <span
          className={[
            styles.typeCell,
            n.resolved_at !== null
              ? styles.typeCleared
              : n.trigger === 'overdue'
                ? styles.typeOverdue
                : n.trigger === 'due_soon'
                  ? styles.typeDueSoon
                  : '',
          ]
            .filter(Boolean)
            .join(' ')}
        >
          {/* D5.2 (Y-3), раунд 2 гейта — маркер непрочитанности в НАЧАЛЕ строки.
              Веса текста (500 против 400) на 14px Inter оказалось мало: ревьюер
              назвал его «на пороге различимости», и фактическим носителем
              состояния оставалась точка в колонке «Прочтение» — за 1900px от
              заголовка. Точка здесь не спорит с полосой выделения: у них разные
              место, форма и цвет, и полоса по-прежнему значит ровно одно.
              Слот держит ширину и у прочитанных строк, иначе колонка «Тип»
              съезжала бы на 12px от строки к строке. */}
          <span
            className={[styles.unreadDot, n.read_at === null ? styles.unreadDotOn : '']
              .filter(Boolean)
              .join(' ')}
            aria-hidden="true"
          />
          {/* Терминальное состояние обязано читаться и в «Типе»: чип «Неактуально»
              живёт на ~400px правее, и при скане этой колонки закрытый эпизод
              выглядел активным «Просрочено». */}
          <span className={styles.typeIcon} aria-hidden="true">
            {n.resolved_at !== null ? <CircleSlash size={15} /> : triggerIcon(n.trigger, 15)}
          </span>
          {notifLabel(n)}
        </span>
      ),
    },
    {
      id: 'task',
      header: m.tm_notif_col_task(),
      // D5.1 (Y-2) — «Задача» стала колонкой-поглотителем (без `width`).
      // Одного скрытия «Кто» и «Событий» не хватало: при `layout=fixed` и всех
      // колонках с заданной шириной остаток ширины делится МЕЖДУ НИМИ
      // пропорционально, поэтому 558px мёртвого места просто переехали в
      // «Проект» и «Тип». Поглотитель адресует остаток туда, где он работает:
      // заголовок задачи — единственное здесь поле переменной длины.
      // `resizable: false` обязателен: присвоение ширины убивает поглощение.
      resizable: false,
      // С-G Р.6 — пол поглотителя: при 1280 соседи съедали остаток, и предмет строки (задача,
      // документ заявки) был 0 px. Ниже пола таблица уходит в горизонтальную прокрутку.
      minWidth: 240,
      accessor: (n) => (
        <span className={styles.taskCell}>
          {/* F8 — у дайджеста задачи нет: предмет строки — проект и счётчики.
              Общий гейт `task_title !== null` увёл бы его в «Задача недоступна».
              B12 — у отмены проекта задачи тоже нет, но тело у неё СВОЁ: сколько
              задач адресата закрылось. Ветка дайджеста вывела бы здесь
              «Просрочено: 0 · срок приближается: 0» — свод там, где его нет. */}
          {isDigest(n) ? (
            <span className={styles.taskTitle} title={n.project_name ?? undefined}>
              {m.tm_notif_digest_counts({
                overdue: numParam(n.params?.['overdue_count']),
                dueSoon: numParam(n.params?.['due_soon_count']),
              })}
            </span>
          ) : n.trigger === 'project_cancelled' ? (
            <span className={styles.taskTitle} title={n.project_name ?? undefined}>
              {m.tm_notif_project_cancelled_body({
                count: numParam(n.params?.['cancelled_task_count']),
              })}
            </span>
          ) : n.trigger === 'subtree_cancelled' ? (
            // F13 — предмет ПАПКА, а не проект: заголовок несёт имя этапа, тело —
            // число отменённых задач. Без своей ветки строка показывала бы имя
            // задачи и молчала о том, что произошло с содержимым.
            <span className={styles.taskTitle} title={n.task_title ?? undefined}>
              {m.tm_notif_subtree_cancelled_body({
                count: numParam(n.params?.['cancelled_task_count']),
              })}
            </span>
          ) : n.trigger === 'timer_reminder' ? (
            // F31 (§3.8) — предмет задача, но человеку важно ЧИСЛО ЧАСОВ: имя
            // задачи он и так помнит, а «уже девять часов» — это то, ради чего
            // напоминание пришло. Часы — structural-параметр от B21, текст
            // собирается здесь.
            <span className={styles.taskTitle} title={n.task_title ?? undefined}>
              {m.tm_notif_timer_reminder_body({
                hours: numParam(n.params?.['running_hours']),
                task: n.task_title ?? m.tm_notif_task_unavailable(),
              })}
            </span>
          ) : accessRequestModule(n) ? (
            // С-I2 Р.7: предмет просьбы о доступе — раздел, а не «Задача недоступна».
            <span
              className={[styles.taskTitle, n.read_at === null ? styles.taskTitleUnread : '']
                .filter(Boolean)
                .join(' ')}
            >
              {m.tm_notif_access_subject({
                module: moduleLabel(accessRequestModule(n) as string),
              })}
            </span>
          ) : isApprovalSubject(n) ? (
            // С-G Р.6 — у заявки нет задачи: предмет — документ заявки, а не «Задача недоступна».
            // Круг 2 гейта: вид действия обрезается, номер документа — нет.
            <span className={styles.approvalSubject} title={approvalSubjectText(n) ?? undefined}>
              <span
                className={[
                  styles.taskTitle,
                  styles.approvalKind,
                  n.read_at === null ? styles.taskTitleUnread : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
              >
                {approvalSubjectParts(n)?.kind}
              </span>
              {approvalSubjectParts(n)?.doc && (
                <span className={styles.approvalDoc}>
                  {approvalSubjectParts(n)?.doc}
                  {(approvalSubjectParts(n)?.more ?? 0) > 0 && ` +${approvalSubjectParts(n)?.more}`}
                </span>
              )}
            </span>
          ) : n.task_title !== null ? (
            <span
              className={[styles.taskTitle, n.read_at === null ? styles.taskTitleUnread : '']
                .filter(Boolean)
                .join(' ')}
              title={n.task_title}
            >
              {n.task_title}
            </span>
          ) : (
            <span className={styles.taskUnavailable}>{m.tm_notif_task_unavailable()}</span>
          )}
          {/* Вторая строка ячейки — ОДНА inline-линия: `.taskCell` выложена
              колонкой, поэтому каждый отдельный span растягивался бы на всю
              ширину (бейдж читался как поле ввода) и добавлял свой этаж. */}
          {(n.task_seq !== null ||
            n.actor_name !== null ||
            episodeDueChip(n, locale) !== null ||
            n.resolved_at !== null) && (
            <span className={styles.taskMeta}>
              {/* F21 (§3.5, UF-11 п.3) — номер задачи при известном коде проекта
                  читается «КОД-42»: ровно так его называют в разговоре, и он
                  опознаёт задачу без открытия. */}
              {!isProjectSubject(n) && n.task_seq !== null && (
                <span className={styles.seq}>
                  {n.project_code !== null ? `${n.project_code}-${n.task_seq}` : `#${n.task_seq}`}
                </span>
              )}
              {/* D5.1 (Y-2) — актор переехал сюда из колонки «Кто»: он есть
                  только у части строк (упоминание, назначение, смена статуса),
                  и отдельная колонка ради него держала 260px, пустых у всей
                  SLA-выдачи. В мета-строке он стоит рядом с предметом, к
                  которому относится, и не занимает места, когда его нет. */}
              {/* Раунд 2 гейта: с уходом колонки «Кто» имя потеряло подпись, и по
                  одному положению в строке его роль не восстанавливается — рядом
                  стоят `#номер` и дата, имя читалось просто «ещё одна мета».
                  Глиф возвращает роль, `title` — словами для читалки. */}
              {n.actor_name !== null && (
                <span className={styles.actorChip} title={m.tm_notif_col_who()}>
                  <User size={12} aria-hidden="true" />
                  {n.actor_name}
                </span>
              )}
              {/* F4 — чип даты эпизода: к какому сроку относится эта SLA-строка.
                  У снятого эпизода дата зачёркнута И рядом стоит актуальный срок:
                  иначе единственное число в ряду — устаревшее, а список для триажа
                  показывает мёртвое значение как единственную правду (UI-ревю С2). */}
              {episodeDueChip(n, locale) !== null && (
                <span className={styles.dueChip}>{episodeDueChip(n, locale)}</span>
              )}
              {n.resolved_at !== null &&
                n.task_due_date !== null &&
                n.task_due_date !== n.params?.['due_date'] && (
                  <span className={styles.dueChipLive}>
                    → {formatIsoDate(n.task_due_date, locale)}
                  </span>
                )}
              {/* Причина прямо в списке: «Неактуально» без неё не отвечает на
                  вопрос, эпизод умер от закрытия задачи или от переноса срока —
                  а это разные реакции пользователя (UX-ревю С2). */}
              {/* В чипе — ТОЛЬКО причина: сам факт конца эпизода уже назван в
                  колонке «Тип» («Просрочено · снято»). Два словаря для одного
                  состояния в одной строке читаются как два разных факта. */}
              {n.resolved_at !== null && (
                <span className={styles.staleChip}>
                  {n.resolve_reason !== null
                    ? m.tm_notif_resolve_reason(n.resolve_reason)
                    : m.tm_notif_resolved_badge()}
                </span>
              )}
            </span>
          )}
        </span>
      ),
    },
    {
      id: 'project',
      header: m.tm_notif_col_project(),
      sortable: true,
      // D5.1 — 160 обрезали чип «Склад — приёмка 50» на 4px (замер scrollWidth):
      // до появления поглотителя обрезки не было, потому что колонка добирала
      // ширину дележом остатка.
      width: 200,
      accessor: (n) =>
        n.project_name !== null ? (
          /* F21 — НАЗВАНИЕ несёт смысл, КОД опознаёт: человек видел один код и не
             понимал, о каком проекте речь. Код идёт mono и приглушённым. */
          <span
            className={styles.chipTag}
            title={
              n.project_code !== null ? `${n.project_name} · ${n.project_code}` : n.project_name
            }
          >
            {n.project_name}
            {n.project_code !== null && <span className={styles.chipCode}>{n.project_code}</span>}
          </span>
        ) : (
          <span className={styles.muted}>—</span>
        ),
    },
    {
      // D5.1 (Y-2) — колонка скрыта в дефолтном пресете (см. стор): у всей
      // SLA-выдачи `actor_name` пуст, потому что уведомление рождает джоб.
      // Актор, где он есть, показан в мета-строке «Задачи» — колонка осталась
      // для того, кому нужна табличная форма (меню «Колонки»).
      id: 'who',
      header: m.tm_notif_col_who(),
      width: 150,
      accessor: (n) =>
        n.actor_name !== null ? n.actor_name : <span className={styles.muted}>—</span>,
    },
    {
      // D5.1 (Y-2) — колонка скрыта в дефолтном пресете (см. стор): её значение —
      // строгая функция подписи в «Типе» (`×2` ровно там, где написано
      // «Просрочено · повторно»), то есть 298px под второе начертание того же факта.
      id: 'count',
      header: m.tm_notif_col_events(),
      sortable: true,
      align: 'right',
      // Ширина под label «Событий» + 60px affordance-reveal (hover/active sort),
      // иначе label ellipsis-клипается до «Событ» при активной сортировке (UI-review).
      width: 172,
      accessor: (n) => {
        // SLA-строка — состояние, а не событие: `count` для неё не инкрементится
        // (иначе «×7 → ×9» на каждом 15-минутном прогоне). Но накопление у эпизода
        // ЕСТЬ — это его стадия: initial = 1 срабатывание, reminder_3d = 2
        // (первый аларм + единственная эскалация через 3 дня). Раньше колонка
        // показывала «—» даже у строк «Просрочено · повторно» (UI/UX-ревю С2).
        const c = episodeEventCount(n);
        // «—» в этой колонке означало бы «нет данных» — ровно как в соседней «Кто».
        // Одно срабатывание — это ЗНАЧЕНИЕ, поэтому пишем «×1» приглушённо, а
        // прочерк оставляем только там, где значения действительно нет.
        return (
          <span
            className={c > 1 ? styles.count : styles.countSingle}
            title={m.tm_notif_col_events_hint()}
          >
            ×{c}
          </span>
        );
      },
    },
    {
      id: 'created_at',
      // «Дата» рядом с чипом «срок 24.07» читалась двусмысленно: это дата
      // получения уведомления, а не срок задачи (UX-ревю С2).
      header: m.tm_notif_col_received(),
      sortable: true,
      align: 'right',
      // F5 `[S-FE-5]` — формат сменился с «1 дн назад» на «20 июл, 14:45»: 130px
      // при nowrap клипали бы дату. Ширина под самый длинный вариант обеих локалей.
      width: 168,
      accessor: (n) => (
        <span className={styles.date} title={notifTimeTitle(n.created_at, locale)}>
          {notifTime(n.created_at, locale)}
        </span>
      ),
    },
    {
      id: 'status',
      // «Прочтение» (не «Статус») — устраняет коллизию с task-статусом карточки (UX-review).
      header: m.tm_notif_col_read_state(),
      // С-G Р.6, круг 2: «Не прочитано» со значком — 136 (замер), освобождённое — предмету.
      width: 136,
      accessor: (n) => {
        const unread = n.read_at === null;
        // D5.3 (Y-4) — ячейка состояния прочтения СРАЗУ его и переключает.
        // Раньше, чтобы отметить одно уведомление, приходилось поставить чекбокс
        // и уйти в панель наверху — круговой маршрут для инбокса, где отметка
        // прочитанным и есть основное действие. Кнопка стоит там, где состояние
        // показано, и не прячется под hover (инвариант I9).
        // Раунд 2 гейта: подпись в строке — СОСТОЯНИЕ единственного числа
        // («Не прочитано»), а не словарь фильтра («Непрочитанные»). Множественное
        // принадлежит воронке колонки, где выбирают выборку; в строке оно
        // читалось как имя категории, а не как то, что переключается. Глиф —
        // конверт: он же и аффорданс, по которому кнопку находят глазами.
        return (
          <button
            type="button"
            className={[
              styles.status,
              styles.statusBtn,
              unread ? styles.statusUnread : styles.statusRead,
            ].join(' ')}
            aria-pressed={!unread}
            title={unread ? m.tm_notif_mark_read_hint() : m.tm_notif_mark_unread_hint()}
            onClick={() => void toggleUnread(n)}
          >
            <span className={styles.statusIcon} aria-hidden="true">
              {unread ? <Mail size={14} /> : <MailOpen size={14} />}
            </span>
            {unread ? m.tm_notif_state_unread() : m.tm_notif_state_read()}
          </button>
        );
      },
    },
    {
      /**
       * F17 (§3.5) [UF-4] — «Скрыть» на строке. Всегда видимая колонка, не
       * hover-only (I9): действие, которое видно только при наведении, для
       * клавиатуры и тач-устройств не существует. В сегменте «Скрытые» та же
       * кнопка возвращает строку в список.
       */
      id: 'dismiss',
      header: '',
      align: 'center',
      width: 56,
      accessor: (n) => (
        <button
          type="button"
          className={styles.navBtn}
          aria-label={showDismissed ? m.tm_notif_undismiss() : m.tm_notif_dismiss()}
          title={showDismissed ? m.tm_notif_undismiss_hint() : m.tm_notif_dismiss_hint()}
          onClick={() => void (showDismissed ? undismiss([n.id]) : dismiss([n.id], notifLabel(n)))}
        >
          {showDismissed ? <Eye size={16} /> : <EyeOff size={16} />}
        </button>
      ),
    },
    {
      // Всегда видимая колонка прямого перехода к задаче (I9 — не hover-only).
      id: 'goto',
      header: '',
      align: 'center',
      width: 56,
      accessor: (n) =>
        // F8 — у дайджеста переход ведёт не в задачу, а в префильтрованный центр.
        isDigest(n) && n.task_project_id !== null ? (
          <button
            type="button"
            className={styles.navBtn}
            aria-label={m.tm_notif_digest_go_project()}
            title={m.tm_notif_digest_go_project()}
            onClick={() => goToProjectNotifications(n.task_project_id as string)}
          >
            <ArrowUpRight size={15} />
          </button>
        ) : n.trigger === 'project_cancelled' && n.task_project_id !== null ? (
          // B12 — отмена ведёт в КАРТОЧКУ проекта, а не в префильтр «просрочено /
          // срок приближается»: задачи закрыты, сроков там больше нет, и фильтр
          // дайджеста открыл бы заведомо пустой список.
          <button
            type="button"
            className={styles.navBtn}
            aria-label={m.tm_notif_go_project_card()}
            title={m.tm_notif_go_project_card()}
            onClick={() => {
              void navigate({
                to: '/projects/$id',
                params: { id: n.task_project_id as string },
              });
            }}
          >
            <ArrowUpRight size={15} />
          </button>
        ) : approvalIdOf(n) ? (
          // F93 (Ф6): уведомление о заявке уровня 3 ведёт на заявку в очереди согласований.
          <button
            type="button"
            className={styles.navBtn}
            aria-label={m.tm_notif_go_request()}
            title={m.tm_notif_go_request()}
            onClick={() => {
              void navigate({ to: '/approvals', search: { request: approvalIdOf(n) } as never });
            }}
          >
            <ArrowUpRight size={15} />
          </button>
        ) : n.task_id !== null ? (
          <button
            type="button"
            className={styles.navBtn}
            aria-label={m.tm_notif_card_go_to_task()}
            title={m.tm_notif_card_go_to_task()}
            onClick={() => goToTask(n)}
          >
            <ArrowUpRight size={15} />
          </button>
        ) : null,
    },
  ];

  // С-G Р.6, круг 2 (UX MAJOR) — колонка «Проект» пустая во всех строках страницы (например,
  // только заявки уровня 3) место у предмета не отнимает.
  const noProjectOnPage = rows.length > 0 && rows.every((n) => !n.project_name);
  const visibleColumns = allColumns.filter(
    (c) => !hidden.includes(c.id) && !(c.id === 'project' && noProjectOnPage),
  );

  const controlledSort = sort ? { id: sort.id as string, dir: sort.dir } : null;
  const onSortChange = (next: { id: string; dir: 'asc' | 'desc' } | null): void => {
    if (next === null) {
      setSort(null);
      return;
    }
    if (!SORTABLE.has(next.id as ColId)) return;
    setSort(next as NonNullable<NotifTableSort>);
  };

  const columnWidths = useMemo(() => new Map(Object.entries(widths)), [widths]);

  // Per-column фильтры (Тип / Статус / Проект).
  const renderColumnFilter = (col: TableColumn<NotificationItem>): JSX.Element | null => {
    if (col.id === 'trigger') {
      return (
        <NotifColumnFilter
          ariaLabel={m.tm_notif_col_type()}
          options={TRIGGER_OPTIONS}
          selected={triggerFilter}
          onToggle={(v) =>
            setTriggerFilter((prev) =>
              prev.includes(v) ? prev.filter((x) => x !== v) : [...prev, v],
            )
          }
        />
      );
    }
    if (col.id === 'project' && projectOptions.length > 0) {
      return (
        <NotifColumnFilter
          ariaLabel={m.tm_notif_col_project()}
          options={projectOptions}
          selected={projectFilter ? [projectFilter] : []}
          onToggle={(v) => setProjectFilter((prev) => (prev === v ? null : v))}
        />
      );
    }
    if (col.id === 'status') {
      // Единая воронка (как Тип/Проект), single-select unread/read (UI-review: не ✓).
      return (
        <NotifColumnFilter
          ariaLabel={m.tm_notif_col_read_state()}
          searchThreshold={99}
          options={[
            { value: 'unread', label: m.tm_notif_status_unread() },
            { value: 'read', label: m.tm_notif_status_read() },
          ]}
          selected={statusFilter ? [statusFilter] : []}
          onToggle={(v) => setStatusFilter((prev) => (prev === v ? null : (v as StatusFilter)))}
        />
      );
    }
    return null;
  };

  return (
    <div ref={pageRef} className={[shared.page, styles.pageWidth].join(' ')}>
      <PageHeader
        title={m.tm_notif_center_title()}
        actions={
          tab === 'list' ? (
            <>
              {/* Раунд 3 (UX): «Прочитать все» в ящике «Скрытые» не объявляло
                  области действия — прочитает скрытые или весь центр. В ящике
                  действие снято: разбирают инбокс, а не архив. */}
              {!showDismissed && (
                <Button variant="ghost" size="sm" onClick={() => void readAll()}>
                  <CheckCheck size={15} /> {m.tm_notif_bulk_all_read()}
                </Button>
              )}
              {/* F17 [UF-4] — то, ради чего заводилась ось: прочитанные копятся
                  вечно, и убирать их по одной никто не станет. Непрочитанные эта
                  кнопка не трогает никогда — иначе «скрыть прочитанные» означало
                  бы «потерять то, что не успел прочесть». */}
              {!showDismissed && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => void dismissRead()}
                  title={m.tm_notif_dismiss_read_hint()}
                >
                  <EyeOff size={15} /> {m.tm_notif_dismiss_read()}
                </Button>
              )}
            </>
          ) : undefined
        }
      />

      {/* F11 (§9.2) — вкладка настроек живёт в URL (`?tab=settings`), вкладки
          списка ниже остаются useState: осознанная асимметрия, перевод всех
          вкладок центра на URL — отдельная тема `[S-C4-10]`, §8 п.13. */}
      <div className={styles.tabs} role="tablist">
        {(
          [
            ['list', m.tm_notif_tab_list()],
            ['settings', m.tm_notif_tab_settings()],
          ] as const
        ).map(([key, label]) => (
          <button
            key={key}
            type="button"
            role="tab"
            aria-selected={tab === key}
            className={[styles.tab, tab === key ? styles.tabActive : ''].join(' ')}
            onClick={() =>
              void navigate({
                to: '/notifications',
                search: key === 'settings' ? { tab: 'settings' } : {},
              })
            }
          >
            {label}
          </button>
        ))}
      </div>

      {tab === 'settings' ? (
        <NotificationSettingsPanel />
      ) : (
        <>
          {/* Оси: пресеты (§3.3) + меню «Колонки». */}
          <div className={styles.toolbar}>
            <div className={styles.segment} role="tablist">
              {(
                [
                  ['all', m.tm_notif_filter_all(), segmentCounts?.all],
                  ['actionable', m.tm_notif_filter_actionable(), segmentCounts?.actionable],
                  ['info', m.tm_notif_filter_info(), segmentCounts?.info],
                ] as const
              ).map(([key, label, count]) => (
                <button
                  key={key}
                  type="button"
                  role="tab"
                  aria-selected={mode === key}
                  className={[styles.chip, mode === key ? styles.chipActive : ''].join(' ')}
                  // Раунд 2 гейта — база счётчика названа словами. Колокольчик
                  // считает НЕПРОЧИТАННЫЕ, сегмент — все записи текущей выборки,
                  // и без объяснения два разных числа на одном экране читались
                  // как расхождение (тот же класс, что DR-F6).
                  title={m.tm_notif_seg_count_hint()}
                  onClick={() => setMode(key)}
                >
                  {label}
                  {/* Счётчик появляется только когда сервер его прислал: ноль
                      «на всякий случай» читался бы как «здесь пусто». */}
                  {count !== undefined && <span className={styles.chipCount}>{count}</span>}
                </button>
              ))}
            </div>
            {/* F17 [UF-4] — «Скрытые» ОТДЕЛЬНЫМ чипом, а не четвёртым значением оси
                содержимого: скрытые не пересекаются ни с «Требует действия», ни с
                «Для сведения» — это не срез выдачи, а другой ящик, и возвращают
                строки именно отсюда. */}
            <button
              type="button"
              aria-pressed={showDismissed}
              /* Раунд 1 (UI): дословное переиспользование `.chip` делало «Скрытые»
                 четвёртым сегментом содержимого — тот же кегль, та же пилюля, та
                 же активная заливка. У ящика собственная форма: контур вместо
                 заливки и отступ от группы сегментов. */
              className={[
                styles.dismissedChip,
                showDismissed ? styles.dismissedChipActive : '',
              ].join(' ')}
              onClick={() => {
                setShowDismissed((v) => !v);
                setSelected(new Set());
              }}
            >
              {/* Раунд 2 (UI): eye-off нёс три смысла на одном экране — действие в
                  шапке, фильтр в чипе и действие в строке. За чипом закреплён ящик
                  (архив), за eye-off — только само скрытие. */}
              <Archive size={16} /> {m.tm_notif_segment_dismissed()}
              {dismissedCount !== null && (
                <span className={styles.chipCount}>{dismissedCount}</span>
              )}
            </button>
            {/* F6 — «Показать неактуальные»: резолвнутые эпизоды скрыты по умолчанию
            (центр — про актуальное), но история доступна одним кликом. */}
            {/* Подпись отдаём самому примитиву: он уже рендерит собственный
                <label> с htmlFor, а внешняя обёртка давала вложенные label. */}
            <Checkbox
              className={styles.resolvedToggle}
              checked={showResolved}
              onChange={() => setShowResolved((v) => !v)}
              label={m.tm_notif_show_resolved()}
            />
            {/* Сегмент называется «Все», но по умолчанию скрывает резолвнутые —
            без явной подписи выборка читается как полная (UX-ревю С2). */}
            {!showResolved && (
              <span className={styles.resolvedHint}>{m.tm_notif_stale_hidden()}</span>
            )}
            <div className={styles.toolbarSpacer} />
            {/* Раунд 2 гейта — клавиши строки объявлены НА ЭКРАНЕ. Прежде их
                знал только код: `Esc` показывался в панели, но лишь после
                выделения, а `x` и `e` не показывались нигде. Клавиша, о которой
                нигде не написано, для пользователя не существует. */}
            <span className={styles.kbdHint}>
              <kbd className={styles.bulkKbd}>x</kbd> {m.tm_notif_kbd_select()}
              <span className={styles.kbdSep}>·</span>
              <kbd className={styles.bulkKbd}>e</kbd> {m.tm_notif_kbd_toggle_read()}
              <span className={styles.kbdSep}>·</span>
              {/* Клавиша, о которой нигде не написано, для пользователя не существует. */}
              <kbd className={styles.bulkKbd}>h</kbd>{' '}
              {showDismissed ? m.tm_notif_kbd_undismiss() : m.tm_notif_kbd_dismiss()}
            </span>
            <TaskPopover
              width={220}
              align="end"
              trigger={(open, isOpen) => (
                <Button variant="ghost" size="sm" onClick={open} aria-expanded={isOpen}>
                  <Columns3 size={15} /> {m.tm_notif_columns_btn()}
                </Button>
              )}
            >
              {() => (
                <div className={styles.colMenu}>
                  {allColumns.map((col) => (
                    <label key={col.id} className={styles.colMenuItem}>
                      <Checkbox
                        checked={!hidden.includes(col.id)}
                        onChange={() => toggleHidden(col.id)}
                        aria-label={typeof col.header === 'string' ? col.header : col.id}
                      />
                      {col.header}
                    </label>
                  ))}
                </div>
              )}
            </TaskPopover>
          </div>

          {/* Раунды 2–3 (UX): обратимость объявлялась только пустым состоянием —
              то есть там, где скрытых нет; а посаженная в панель осей строка
              читалась подписью чужого чекбокса. Здесь она отдельной строкой над
              таблицей и принадлежит только ящику. */}
          {showDismissed && (
            <p className={styles.dismissedHint}>{m.tm_notif_dismissed_hint_row()}</p>
          )}
          <Table<NotificationItem>
            className={selected.size > 0 ? styles.listWithBulk : undefined}
            data={rows}
            columns={visibleColumns}
            rowKey={(n) => n.id}
            ariaLabel={m.tm_notif_center_title()}
            loading={query.isLoading}
            layout="fixed"
            selectable
            selectedIds={selected}
            onSelectionChange={setSelected}
            controlledSort={controlledSort}
            onSortChange={onSortChange}
            /* F18 (раунд 7 гейта) — панель получает ОЧЕРЕДЬ, в которой открыта
               строка: инбокс разбирают потоком, и «обработал → вернись в список →
               найди следующую» этот поток рвало. */
            onRowClick={(row) => openCard(row, rows)}
            onRowKeyDown={onRowKey}
            // Table резолвит имя как КЛЮЧ своего CSS-модуля, поэтому состояние живёт
            // в примитиве (`rowMuted`), а не в модуле маршрута. Резолвнутый эпизод
            // важнее непрочитанности: мёртвая строка не носит акцентный маркер.
            rowClassName={(n) =>
              n.resolved_at !== null ? 'rowMuted' : n.read_at === null ? 'rowUnread' : undefined
            }
            resizableColumns
            columnWidths={columnWidths}
            onColumnResize={setWidth}
            renderColumnFilter={renderColumnFilter}
            /* F17 — у сегмента «Скрытые» своё пустое состояние: «Пока нет
               уведомлений» здесь читалось бы как «центр пуст», хотя пуст ровно
               этот ящик, и объяснять надо не периметр компании, а что скрытое
               отсюда возвращается. */
            emptyTitle={showDismissed ? m.tm_notif_dismissed_empty() : m.tm_notif_empty_title()}
            emptyDescription={
              showDismissed ? m.tm_notif_dismissed_empty_hint() : m.tm_notif_entity_scope_hint()
            }
            emptyAction={
              <Button
                variant="secondary"
                size="sm"
                // F12 — CTA ведёт на вкладку настроек центра, а не в профиль.
                onClick={() => void navigate({ to: '/notifications', search: { tab: 'settings' } })}
              >
                {m.tm_notif_empty_cta()}
              </Button>
            }
          />

          {/* Раунд 3 гейта — предел выдачи назван словами. Пагинация центра
              перешла в offset-режим вместе с сортировкой по критичности, и
              «Показать ещё» без знаменателя не отвечает, сколько ещё осталось. */}
          {loadedTotal !== null && (
            <div className={styles.countRow}>
              {m.tm_notif_shown_of({ shown: rows.length, total: loadedTotal })}
            </div>
          )}

          {query.hasNextPage && (
            <div className={styles.moreRow}>
              <Button
                variant="ghost"
                size="sm"
                disabled={query.isFetchingNextPage}
                onClick={() => void query.fetchNextPage()}
              >
                {m.tm_notif_show_more()}
              </Button>
            </div>
          )}

          {/* D5.3 (Y-4) — панель массовых действий у нижней кромки, а не над
              таблицей. Сверху она сдвигала весь список в момент выделения: строка,
              по которой только что кликнули, уезжала из-под курсора. Док стоит в
              конце страницы (иначе `sticky bottom` не к чему прижиматься) и внутри
              центрирующего контейнера — тогда центр панели совпадает с центром
              таблицы. «Сбросить» и подсказка Esc — там же: выход из режима
              выделения обязан быть виден, а не угадываться. */}
          <div className={styles.bulkDock}>
            {selected.size > 0 && (
              <div
                ref={bulkBarRef}
                className={styles.bulkBar}
                role="region"
                aria-label={m.tm_notif_bulk_region()}
              >
                {/* Раунд 4 гейта — у отмеченного появился знаменатель. «Выбрано: 2»
                    не отвечало, из скольких; после прокрутки состояние выделения
                    восстановить было нечем. */}
                <span className={styles.bulkCount}>
                  {loadedTotal !== null
                    ? m.tm_notif_selected_of({ count: selected.size, total: loadedTotal })
                    : m.tm_notif_selected({ count: selected.size })}
                </span>
                <Button variant="primary" size="sm" onClick={() => void bulkRead()}>
                  <Check size={14} /> {m.tm_notif_bulk_read()}
                </Button>
                {/* Раунд 1 (UI): действие, ради которого заводилась задача, стояло
                    третьим и было легче соседей — глаз ловил его последним. В
                    сегменте «Скрытые» та же кнопка возвращает: действие всегда
                    противоположно ящику, в котором стоишь. */}
                <Button variant="secondary" size="sm" onClick={() => void bulkDismiss()}>
                  {showDismissed ? <Eye size={14} /> : <EyeOff size={14} />}{' '}
                  {showDismissed ? m.tm_notif_bulk_undismiss() : m.tm_notif_bulk_dismiss()}
                </Button>
                <Button variant="ghost" size="sm" onClick={() => void bulkUnread()}>
                  <Undo2 size={14} /> {m.tm_notif_bulk_unread()}
                </Button>
                <span className={styles.bulkSpacer} />
                <Button variant="ghost" size="sm" onClick={() => setSelected(new Set())}>
                  <X size={14} /> {m.tm_notif_bulk_clear()}
                </Button>
                <kbd className={styles.bulkKbd}>Esc</kbd>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function numParam(v: unknown): number {
  return typeof v === 'number' ? v : 0;
}
